import { useContext, useState, useMemo, useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { BoxFull, Button, Dropdown } from "iuvity-ui-components";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/index";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { REVALIDATE_FORM_PROPS, datatypeOfRegister } from "../../constants";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { InputText } from "iuvity-ui-components/dist/inputtext";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { Column } from "primereact/column";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { FilterMatchMode } from "primereact/api";
import { CustomDialog, DialogProps } from "../dialog/CustomDialog";
import { useMasters } from "../../hooks/useMasters";
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import { GoogleMap } from "./GoogleMap";
import { mastersFormatter, pointOfAttentionFormatter } from "../../utils";
import { getPointOfAttentionSearch } from "../../services/configurations/pointsOfAttention/pointsofattention";
import { usePointOfAttention } from "../../hooks/usePointsOfAttention";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";
import { PrivilegeValidatorComponent } from "../shared/PrivilegeValidatorComponent";

const formBlankValues = {
  typeOfPointAttention: "",
  typeOfRegister: "",
  code: "",
  name: "",
  pointServiceType: "",
  country: "",
  city: "",
  startOfValidity: "",
  endOfValidity: "",
  recordType: "",
};

export const AdminSearchPointsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { language, selectedApp, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });

  const { control, setValue } = form;

  const typeOfPointAttentionValues = useWatch({
    control: control,
    name: "typeOfPointAttention",
  });

  const typeOfRegisterValues = useWatch({
    control: control,
    name: "typeOfRegister",
  });

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "select",
          header: "",
        },
        {
          field: "code",
          header: t("PointsOfAttention.codeLabel"),
        },
        {
          field: "name",
          header: t("PointsOfAttention.nameLabel"),
        },
        {
          field: "pointServiceType",
          header: t("PointsOfAttention.typeLabel"),
        },
        {
          field: "country",
          header: t("PointsOfAttention.countryLabel"),
        },
        {
          field: "city",
          header: t("PointsOfAttention.cityLabel"),
        },
        {
          field: "startOfValidity",
          header: t("PointsOfAttention.startOfValidityLabel"),
        },
        {
          field: "endOfValidity",
          header: t("PointsOfAttention.endOfValidityLabel"),
        },
        {
          field: "recordType",
          header: t("PointsOfAttention.recordTypeLabel"),
        },
        {
          field: "actions",
          header: t("PointsOfAttention.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const { dataMaster } = useMasters("DATA");
  const { pointOfAttentionAction, reloadFnPointOfAttention } = usePointOfAttention("DATA");
  const formKeysOptions: any = {
    typeOfPointAttention: mastersFormatter(dataMaster),
    typeOfRegister: datatypeOfRegister(t),
  };
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [administrationFilters, setAdministrationFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    code: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    pointServiceType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    country: { value: null, matchMode: FilterMatchMode.CONTAINS },
    city: { value: null, matchMode: FilterMatchMode.CONTAINS },
    startOfValidity: { value: null, matchMode: FilterMatchMode.CONTAINS },
    endOfValidity: { value: null, matchMode: FilterMatchMode.CONTAINS },
    recordType: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const [data, setData] = useState<any>([]);
  const [showTable1, setShowTable1] = useState(false);
  const [showTable2, setShowTable2] = useState(false);
  const [selectRows, setSelectRows] = useState([]);
  const [disabledMultiple, setDisabledMultiple] = useState(false);
  const [locations, setLocations] = useState<any>([]);
  const [showMap, setShowMap] = useState(false);
  const apiKey = process.env.REACT_APP_API_KEY_MAP || "";

  const onAction = (type: "DETAIL" | "UPDATE" | "UPDATEMULTIPLE", data?: any) => {
    if (type === "UPDATE" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedPointsObject: data,
      }));
      navigate("/pointsofattention/editpointofattention");
    }
    if (type === "DETAIL" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedPointsObject: data,
      }));
      navigate("/pointsofattention/detailpointofattention");
    }
    if (type === "UPDATEMULTIPLE") {
      setAppState((prev) => ({
        ...prev,
        selectedPointsObject: locations?.map((location: any) => location.id),
      }));
      navigate("/pointsofattention/editmultiplepointofattention");
    }
  };

  const onDeleteAction = async (data: any) => {
    await pointOfAttentionAction("DELETE", data.id);
    handleShowTable();
    setLocations([]);
    setShowMap(false);
  };

  const handleModalProps = async (data: any) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("PointsOfAttention.deleteModalPointsTitle"),
      content: `${t("PointsOfAttention.deleteModalPointsContent1Label")} "${
        data?.name
      }" ${t("PointsOfAttention.deleteModalPointsContent2Label")}`,
      onConfirm: () => onDeleteAction(data!),
    }));
  };

  const handleShowClear = (key: any) => {
    if (key === 'typeOfPointAttention') {
      if (typeOfPointAttentionValues) {
        return true;
      }
      setValue("typeOfRegister", "");
      return false;
    }
    if (key === 'typeOfRegister') {
      if (typeOfRegisterValues) {
        return true;
      }
      return false;
    }
  };

  const handleShowTable = async () => {
    if (typeOfPointAttentionValues && !typeOfRegisterValues) {
      const params = {
        pointServiceTypeId: typeOfPointAttentionValues
      };
      const { data } = await getPointOfAttentionSearch(params, selectedApp?.tenantid!, selectedApp?.appid!)
      setData(pointOfAttentionFormatter(data, t));
      setSelectRows([]);
      setShowTable1(true);
      setShowTable2(false);
      setShowMap(false);
    }
    if (typeOfPointAttentionValues && typeOfRegisterValues) {
      const params = {
        pointServiceTypeId: typeOfPointAttentionValues,
        recordType: typeOfRegisterValues
      };
      const { data } = await getPointOfAttentionSearch(params, selectedApp?.tenantid!, selectedApp?.appid!);
      setData(pointOfAttentionFormatter(data, t));
      setSelectRows([]);
      setShowTable1(false);
      setShowTable2(true);
      setShowMap(false);
    }
  };

  const onClearFilters = () => {
    setAdministrationFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      code: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      pointServiceType: { value: null, matchMode: FilterMatchMode.CONTAINS },
      country: { value: null, matchMode: FilterMatchMode.CONTAINS },
      city: { value: null, matchMode: FilterMatchMode.CONTAINS },
      startOfValidity: { value: null, matchMode: FilterMatchMode.CONTAINS },
      endOfValidity: { value: null, matchMode: FilterMatchMode.CONTAINS },
      recordType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    setShowTable1(false);
    setValue("typeOfPointAttention", "");
    setValue("typeOfRegister", "");
    form.reset(formBlankValues)
    setSelectRows([]);
    setShowTable2(false);
    setLocations([]);
    setShowMap(false);
  };

  const onClearFiltersColumn = () => {
    setAdministrationFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      code: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      pointServiceType: { value: null, matchMode: FilterMatchMode.CONTAINS },
      country: { value: null, matchMode: FilterMatchMode.CONTAINS },
      city: { value: null, matchMode: FilterMatchMode.CONTAINS },
      startOfValidity: { value: null, matchMode: FilterMatchMode.CONTAINS },
      endOfValidity: { value: null, matchMode: FilterMatchMode.CONTAINS },
      recordType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.resetField("code")
    form.resetField("name")
    form.resetField("pointServiceType")
    form.resetField("country")
    form.resetField("city")
    form.resetField("startOfValidity")
    form.resetField("endOfValidity")
    form.resetField("recordType")
    setLocations([]);
    setShowMap(false);
  };

  const handleSelectRows = (value: any) => {
    if (value !== null) {
      setSelectRows(value);
      if (Array.isArray(value)) {
        const newArrays = value.map((item) => {
          return {
            ...item,
            lat: +item.latitude,
            lng: +item.longitude,
          }
        })
        setLocations(newArrays);
      } else {
        setLocations([{
          ...value,
          lat: +value?.latitude,
          lng: +value?.longitude 
        }]);
      }
      setShowMap(true);
    } else {
      setSelectRows([]);
      setShowMap(false);
    }

    if (value?.length === 0) {
      setSelectRows([]);
      setShowMap(false);
    }
  }

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: col.field === 'select' ? ' ' : t(`PointsOfAttention.${col.field}Label`),
        }))
      );
    
      if (selectRows.length > 0) {
        setDisabledMultiple(false)
      } else {
        setDisabledMultiple(true)
      }
  }, [t, language, setSelectedColumns, selectRows]);

  const render = (status: Status) => (<div>{status}</div>);

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`PointsOfAttention.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`PointsOfAttention.${options.field}Placeholder`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  }

  const filterClearTemplate = (options: any) => {
      return (
        <Button
          label={t("ValidationMessages.clearFilterLabel")}
          variant="p-button-text"
          onClick={onClearFiltersColumn}
        />
      )
  }
    
  const filterApplyTemplate = (options: any) => {
      return (
        <Button
          label={t("ValidationMessages.applyFilterLabel")}
          className="p-button"
          onClick={options.filterApplyCallback}
        />
      )
  }

  return (
    <>
      <div className="points__tabdescription">
        <span>{t("PointsOfAttention.administrationOneDescription")}</span>
        <br />
        <span>{t("PointsOfAttention.administrationTwoDescription")}</span>
      </div>
      <SubheaderActions
        className="px-0 my-3"
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "444",
            icon: "iu iu-icon-plus",
            label: t("PointsOfAttention.newAdministrationBtnLabel"),
            style: {
              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
            },
            onClick: () => navigate("/pointsofattention/newpointofattention"),
          },
        ])}
      />
      <BoxFull className="dt-c px-0 py-0 mx-0 my-3" variant="tertiary">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="form-container md py-5 w-full">
            <div className="form md w-full">
              <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
                {Object.keys(formBlankValues).map((key, index) => (
                  <div
                    key={`c-${key}`}
                    className={`flex flex-column mb-0 px-6 ${key === "typeOfRegister" && (typeOfPointAttentionValues === "" || typeOfPointAttentionValues === undefined) ? 'p-disabled' : ""}`}
                    style={{ width: "50%" }}
                  >
                    <Controller
                      name={key as any}
                      control={control}
                      // rules={(PointsServiceSchema(t) as any)[key]}
                      render={({ field }) => {
                        field.onChange = (e) => {
                          setValue(
                            field.name,
                            e.target.value,
                            REVALIDATE_FORM_PROPS
                          );
                        };
                        return [
                          "typeOfPointAttention",
                          "typeOfRegister",
                        ].includes(key) ? (
                          // @ts-ignore
                          <div
                            className="pointsofattention-fields"
                            style={{ paddingLeft: "4rem" }}
                          >
                            <Dropdown
                              key={key}
                              id={`dropdown-${key}`}
                              className={`dropdown mb-2`}
                              label={t(`PointsOfAttention.${key}Label`)}
                              placeholder={t(
                                `PointsOfAttention.${key}Placeholder`
                              )}
                              options={formKeysOptions[key]}
                              showClear={handleShowClear(key)}
                              width="20rem"
                              hasTooltipRef={
                                key === "typeOfRegister" ? true : false
                              }
                              tooltipRef={{
                                content: t(`PointsOfAttention.typeOfRegisterTooltip`),
                                position: "bottom",
                                id: "dropdown-type",
                              }}
                              tooltipIdName="dropdown-type"
                              resetFilterOnHide
                              {...field}
                            />
                          </div>
                        ) : (
                          <></>
                        );
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </BoxFull>
      <BoxFull
        className="my-5 mr-0 p-0"
        style={{ backgroundColor: "transparent" }}
      >
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("PointsOfAttention.administrationClearBtnLabel")}
            onClick={() => onClearFilters()}
          />
          <Button
            variant="p-button"
            label={t("PointsOfAttention.administrationSearchBtnLabel")}
            disabled={!typeOfPointAttentionValues}
            onClick={() => handleShowTable()}
          />
        </div>
      </BoxFull>
      {showTable2 && (
          <>
            <div className="points__tabdescription">
              <span>{t("PointsOfAttention.multipleModificationDescription")}</span>
            </div>
            <BoxFull
              className="my-5 mr-0 p-0"
              style={{ backgroundColor: "transparent" }}
            >
              <div className="flex align-items-center justify-content-end gap-5">
                <PrivilegeValidatorComponent showButton={permissiondInfoByRole.UPDATE} >
                  <Button
                    variant="p-button"
                    label={t("PointsOfAttention.multipleModificationBtnLabel")}
                    disabled={disabledMultiple}
                    onClick={() => onAction("UPDATEMULTIPLE")}
                  />
                </PrivilegeValidatorComponent>
              </div>
            </BoxFull>
          </>
      )}
      {showTable1 && (
        <BoxFull className="dt-c px-0 py-0 mx-0" variant="tertiary">
          {data ? (
            <DataTable
            header={
              <TableHeader
              title={t("PointsOfAttention.pointsTitle")}
              columns={columns}
              selectedColumns={selectedColumns}
              toggleColumns={setSelectedColumns}
              filters={administrationFilters}
              setFilters={setAdministrationFilters}
              reload={reloadFnPointOfAttention}
              hasCheckColumn
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            filters={administrationFilters}
            sortField="id"
            dataKey="id"
            sortOrder={1}
            value={data?.map((configDataSelected: any) => ({
              ...configDataSelected,
              actions: (
                <Actions
                enableDelete={true}
                onDetail={() => onAction("DETAIL", configDataSelected)}
                onEdit={() => onAction("UPDATE", configDataSelected)}
                onDelete={() => handleModalProps(configDataSelected)}
                />
                ),
              }))}
              scrollable
              resizableColumns
              globalFilterFields={[
                "code",
                "name",
                "pointServiceType",
                "country",
                "city",
                "startOfValidity",
                "endOfValidity",
                "recordType",
              ]}
              filterDisplay="menu"
              selection={selectRows}
              onSelectionChange={e => handleSelectRows(e.value)}
              >
              {selectedColumns.map((col: any) => {
                return (
                  <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions" || col.field === "select")}
                  align={col.field === "actions"  ? "center" : "left"}
                  headerStyle={
                    col.field === "actions" 
                    ? { justifyContent: "center" }
                    : undefined
                  }
                  style={{
                    boxShadow:
                    col.field === "actions"
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                    maxWidth: col.field === "actions" ? "8.25rem" : "100%",
                    whiteSpace: "normal",
                    minWidth: col.field === "select" ? "3.5rem" : (col.field === "startOfValidity" || col.field === "endOfValidity" || col.field === "recordType") ? "14rem" : "10rem",
                  }}
                  frozen={col.field === "actions" ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  filterField={col.field}
                  showFilterMatchModes={false}
                  filterMenuStyle={{ width: "23rem" }}
                  filter={col.field === "actions" || col.field === "select" ? false : true}
                  filterElement={filterTemplate}
                  filterApply={filterApplyTemplate}
                  filterClear={filterClearTemplate}
                  selectionMode={col.field === "select" ? 'single' : undefined}
                  />
                  );
                })}
            </DataTable>
          ) : (
            <FormSkeleton length={8} />
            )}
        </BoxFull>
      )}
      {showTable2 && (
        <BoxFull className="dt-c px-0 py-0 mx-0" variant="tertiary">
          {data ? (
            <DataTable
              header={
                <TableHeader
                  title={t("PointsOfAttention.pointsTitle")}
                  columns={columns}
                  selectedColumns={selectedColumns}
                  toggleColumns={setSelectedColumns}
                  filters={administrationFilters}
                  setFilters={setAdministrationFilters}
                  reload={reloadFnPointOfAttention}
                  hasCheckColumn
                />
              }
              currentPageReportTemplate={t(
                "ConsoleTranslations.rowsPaginatorTemplateLabel"
              )}
              paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
              emptyMessage={t("TableActions.emptyMessage")}
              rows={10}
              size="normal"
              filters={administrationFilters}
              sortField="id"
              dataKey="id"
              sortOrder={1}
              value={data?.map((configDataSelected: any) => ({
                ...configDataSelected,
                actions: (
                  <Actions
                    enableDelete={true}
                    onDetail={() => onAction("DETAIL", configDataSelected)}
                    onEdit={() => onAction("UPDATE", configDataSelected)}
                    onDelete={() => handleModalProps(configDataSelected)}
                  />
                ),
              }))}
              scrollable
              resizableColumns
              globalFilterFields={[
                "code",
                "name",
                "pointServiceType",
                "country",
                "city",
                "startOfValidity",
                "endOfValidity",
                "recordType",
              ]}
              filterDisplay="menu"
              selection={selectRows}
              onSelectionChange={e => handleSelectRows(e.value)}
              rowHover
            >
              {selectedColumns.filter((col) => col.field !== 'actions').map((col: any) => {
                return (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable={!(col.field === "actions" || col.field === "select")}
                    align={col.field === "actions"  ? "center" : "left"}
                    headerStyle={
                      col.field === "actions" 
                        ? { justifyContent: "center" }
                        : undefined
                    }
                    style={{
                      boxShadow:
                        col.field === "actions"
                          ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                          : undefined,
                          maxWidth: col.field === "actions" ? "8.25rem" : "100%",
                          whiteSpace: "normal",
                          minWidth: col.field === "select" ? "3.5rem" : (col.field === "startOfValidity" || col.field === "endOfValidity" || col.field === "recordType") ? "14rem" : "10rem",
                    }}
                    frozen={col.field === "actions" ? true : false}
                    alignFrozen={col.field === "actions" ? "right" : undefined}
                    filterField={col.field}
                    showFilterMatchModes={false}
                    filterMenuStyle={{ width: "23rem" }}
                    filter={col.field === "select" ? false : true}
                    filterElement={filterTemplate}
                    filterApply={filterApplyTemplate}
                    filterClear={filterClearTemplate}
                    selectionMode={col.field === "select" ? 'multiple' : undefined}
                  />
                );
              })}
            </DataTable>
          ) : (
            <FormSkeleton length={8} />
          )}
        </BoxFull>
      )}
      {showMap && (
          <>
            <div className="coordinates_title">
              <span>{t("PointsOfAttention.coordinatesTitle")}</span>
            </div>
            <Wrapper apiKey={apiKey} render={render}>
              <GoogleMap locations={locations} t={t} handleModalProps={handleModalProps} setAppState={setAppState} />
            </Wrapper>
          </>
      )}
      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
